/**
 * ServiceTechDashboard — Service Technician (field executive).
 *
 * Ported from `ServiceTechDashboard` in aura-exec.jsx.
 * Today's visit queue + map strip + 11-step workflow stepper for the
 * active ticket + warranty barcode + parts + expense + incentive bar +
 * cold-climate (ITBP) flag (module-gated, NOT tenant-slug gated).
 *
 * UI-only. Tenant-safe.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Sparkles, Users, MapPin, Camera, Package, Wallet, ScanLine } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import {
  safeGet,
  formatINRCompact,
  formatNum,
  todayLabel,
  formatTimeShort,
  makeCreditBurn,
} from './_execShared';

// ─── 11-step workflow (matches backend Service Module spec) ───────────────
const INSTALL_STEPS = [
  'Customer ack',
  'Site survey',
  'Stand + bracket',
  'Tank mount',
  'Plumbing connect',
  'Electrical wire',
  'Leak test',
  'Pressure test',
  'Photo evidence',
  'OTP verify',
  'Warranty activate',
];

// ─── Types ────────────────────────────────────────────────────────────────

interface Visit {
  id: string;
  ticketNumber?: string;
  customerName?: string;
  customer?: { name?: string };
  address?: string;
  location?: string;
  distanceKm?: number;
  km?: number;
  eta?: string;
  etaTime?: string;
  scheduledAt?: string;
  type?: string;
  serviceType?: string;
  currentStep?: number;
  step?: number;
  priority?: 'high' | 'med' | 'medium' | 'low';
  phone?: string;
  isColdClimate?: boolean;
  coldClimate?: boolean;
  notes?: string;
}

interface MonthStats {
  visitsDone?: number;
  onTimePct?: number;
  avgRating?: number;
  expenses?: number;
}

interface IncentivePayload {
  earned?: number;
  target?: number;
  tier?: string;
  nextTier?: string;
}

// ─── Atoms ────────────────────────────────────────────────────────────────

function Skeleton({ h = 60 }: { h?: number }) {
  return (
    <div
      style={{
        height: h,
        background: 'var(--ivory-2)',
        borderRadius: 6,
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    />
  );
}

function NoData({ label = 'No data yet' }: { label?: string }) {
  return <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>{label}</div>;
}

function Chip({
  children,
  color = 'var(--ink-3)',
  bg = 'var(--ivory-2)',
}: {
  children: React.ReactNode;
  color?: string;
  bg?: string;
}) {
  return (
    <span
      style={{
        fontSize: 10,
        padding: '2px 7px',
        borderRadius: 10,
        background: bg,
        color,
        fontWeight: 500,
      }}
    >
      {children}
    </span>
  );
}

function IncentiveBar({ data }: { data: IncentivePayload | null }) {
  if (!data || !data.target) {
    return (
      <div
        className="card"
        style={{
          padding: 14,
          background: 'linear-gradient(180deg, var(--saffron-50), var(--saffron-100))',
          border: '1px solid var(--saffron-100)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--saffron-600)',
            textTransform: 'uppercase',
            letterSpacing: 0.08,
            fontWeight: 600,
          }}
        >
          Incentive tier
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
          No incentive plan configured
        </div>
      </div>
    );
  }
  const earned = data.earned ?? 0;
  const target = data.target ?? 1;
  const pct = Math.min(100, (earned / target) * 100);
  return (
    <div
      className="card"
      style={{
        padding: 16,
        background: 'linear-gradient(180deg, var(--saffron-50), var(--saffron-100))',
        border: '1px solid var(--saffron-100)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 10 }}>
        <div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--saffron-600)',
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              fontWeight: 600,
            }}
          >
            {data.tier ?? 'Bronze'} tier · this month
          </div>
          <div className="num-display" style={{ fontSize: 24, marginTop: 4 }}>
            {formatINRCompact(earned)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
            of {formatINRCompact(target)}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 11, color: 'var(--ink-2)', textAlign: 'right' }}>
          Next: <strong style={{ color: 'var(--saffron-600)' }}>{data.nextTier ?? '—'}</strong>
        </div>
      </div>
      <div style={{ height: 6, background: 'color-mix(in srgb, var(--ivory-0) 50%, transparent)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--saffron)' }} />
      </div>
    </div>
  );
}

function WarrantyBarcodeInput() {
  const [code, setCode] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const lookup = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setResult(null);
    const r = await safeGet<{ status?: string; productName?: string; expiresAt?: string }>(
      `/warranties/by-barcode/${encodeURIComponent(code.trim())}`,
    );
    setLoading(false);
    if (r.error) {
      setResult(`Not found · ${r.error}`);
    } else if (r.data) {
      setResult(
        `${r.data.productName ?? 'Product'} · ${r.data.status ?? 'active'}${r.data.expiresAt ? ' · exp ' + r.data.expiresAt.slice(0, 10) : ''}`,
      );
    } else {
      setResult('Not found');
    }
  };

  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="kpi-label" style={{ marginBottom: 8 }}>
        Warranty barcode lookup
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') lookup();
          }}
          placeholder="Scan or type barcode"
          style={{
            flex: 1,
            padding: '6px 10px',
            border: '1px solid var(--line)',
            borderRadius: 4,
            fontSize: 12,
            fontFamily: 'var(--ff-mono)',
            background: 'var(--ivory-0)',
            color: 'var(--ink-0)',
          }}
        />
        <button className="btn btn-primary btn-sm" onClick={lookup} disabled={loading}>
          <ScanLine size={12} style={{ marginRight: 4 }} />
          {loading ? '…' : 'Look up'}
        </button>
      </div>
      {result && (
        <div style={{ fontSize: 11, color: 'var(--ink-2)', marginTop: 8 }}>
          {result}
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────

export function ServiceTechDashboard({
  onSeeTeam,
}: {
  onSeeTeam?: () => void;
} = {}) {
  const { user, tenant } = useAuthStore();
  const navigate = useNavigate();
  const firstName = user?.firstName ?? 'there';

  const [visits, setVisits] = useState<Visit[]>([]);
  const [loadingVisits, setLoadingVisits] = useState(true);
  const [visitsErr, setVisitsErr] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const [incentive, setIncentive] = useState<IncentivePayload | null>(null);
  const [stats, setStats] = useState<MonthStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const burn = makeCreditBurn();

  useEffect(() => {
    safeGet<{ items?: Visit[] } | Visit[]>('/service/today-visits?technicianId=me').then((r) => {
      const raw = r.data;
      const parsed = Array.isArray(raw) ? raw : (raw as { items?: Visit[] } | null)?.items ?? [];
      setVisits(parsed);
      setActiveId(parsed[0]?.id ?? null);
      setVisitsErr(r.error);
      setLoadingVisits(false);
    });

    safeGet<IncentivePayload>('/role-kpis/service-tech/incentive-progress').then((r) =>
      setIncentive(r.data),
    );

    safeGet<MonthStats>('/role-kpis/service-tech/monthly').then((r) => {
      setStats(r.data);
      setStatsLoading(false);
    });
  }, []);

  // Module-gated cold-climate flag. Check if any visit is flagged as cold-climate
  // AND if the tenant has the `service_cold_climate` module enabled. No slug check.
  const coldClimateVisible = visits.some((v) => v.isColdClimate || v.coldClimate);
  const coldClimateAllowed =
    tenant?.enabledModules === null ||
    tenant?.enabledModules?.includes('service_cold_climate') ||
    tenant?.enabledModules?.includes('service');

  const current = visits.find((v) => v.id === activeId) || visits[0];
  const currentStep = current?.currentStep ?? current?.step ?? 0;

  const getCustomer = (v: Visit) => v.customerName ?? v.customer?.name ?? 'Unknown';
  const getType = (v: Visit) => v.serviceType ?? v.type ?? 'Service';
  const getKm = (v: Visit) => v.distanceKm ?? v.km ?? 0;
  const getEta = (v: Visit) => v.eta ?? v.etaTime ?? formatTimeShort(v.scheduledAt);
  const priAccent = (p: string | undefined) =>
    p === 'high' ? 'var(--danger, #B8563B)' : 'transparent';

  return (
    <div
      className="aura-root"
      style={{ padding: 20, background: 'var(--ivory-0)', minHeight: '100%' }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 14, gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              marginBottom: 3,
            }}
          >
            Service Technician · Field route
          </div>
          <h2 style={{ fontFamily: 'var(--ff-serif)', fontSize: 24, fontWeight: 500 }}>
            Namaste, {firstName}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {todayLabel()} · {visits.length} visit{visits.length === 1 ? '' : 's'} planned
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {onSeeTeam && (
          <button
            className="btn btn-outline btn-sm"
            onClick={onSeeTeam}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Users size={12} /> See team view
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '420px 1fr 300px', gap: 14 }}>
        {/* Visit queue */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ fontSize: 13, fontWeight: 500 }}>Today's visits</h3>
            <div style={{ flex: 1 }} />
            <button
              className="btn btn-ai btn-sm"
              onClick={() => burn(5, 'optimize_route')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Sparkles size={11} /> Optimize · 5 cr
            </button>
          </div>

          {/* Route map placeholder */}
          <div
            className="card"
            style={{
              padding: 0,
              marginBottom: 10,
              overflow: 'hidden',
              position: 'relative',
              height: 140,
              background:
                'linear-gradient(135deg, var(--ivory-3) 0%, var(--ivory-1) 60%, var(--ivory-3) 100%)',
            }}
          >
            <svg
              viewBox="0 0 400 140"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            >
              <path
                d="M20 110 Q100 70 180 80 T360 50"
                stroke="rgba(201,138,59,.5)"
                strokeWidth="2"
                strokeDasharray="3,3"
                fill="none"
              />
              {visits.slice(0, 4).map((v, i) => {
                const x = 20 + i * 110;
                const y = 100 - i * 20;
                return (
                  <g key={v.id}>
                    <circle
                      cx={x}
                      cy={y}
                      r="10"
                      fill={v.id === activeId ? 'var(--saffron)' : 'var(--ink-0)'}
                      stroke="var(--ivory-0)"
                      strokeWidth="2"
                    />
                    <text
                      x={x}
                      y={y + 3}
                      textAnchor="middle"
                      fill="var(--ivory-0)"
                      fontSize="10"
                      fontWeight="600"
                    >
                      {i + 1}
                    </text>
                  </g>
                );
              })}
            </svg>
            <div
              style={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                fontSize: 10,
                color: 'var(--ink-3)',
                background: 'color-mix(in srgb, var(--ivory-0) 80%, transparent)',
                padding: '3px 7px',
                borderRadius: 4,
              }}
            >
              <MapPin size={10} style={{ display: 'inline', marginRight: 3 }} />
              Route preview
            </div>
          </div>

          {loadingVisits ? (
            <Skeleton h={240} />
          ) : visitsErr ? (
            <div className="card" style={{ padding: 14 }}>
              <NoData label="Today's visits unavailable" />
            </div>
          ) : visits.length === 0 ? (
            <div className="card" style={{ padding: 14 }}>
              <NoData label="No visits assigned today" />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {visits.map((v, i) => (
                <div
                  key={v.id}
                  onClick={() => setActiveId(v.id)}
                  className="card"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: 10,
                    cursor: 'pointer',
                    borderLeft: `3px solid ${
                      v.id === activeId ? 'var(--saffron)' : priAccent(v.priority)
                    }`,
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 99,
                      background: v.id === activeId ? 'var(--saffron)' : 'var(--ivory-2)',
                      color: v.id === activeId ? '#1B1713' : 'var(--ink-0)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: 13,
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{getCustomer(v)}</div>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--ink-3)',
                        display: 'flex',
                        gap: 6,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span>{getType(v)}</span>
                      <span>·</span>
                      <Chip>
                        {getKm(v)} km · ETA {getEta(v)}
                      </Chip>
                      {coldClimateAllowed && (v.isColdClimate || v.coldClimate) && (
                        <Chip bg="var(--info-bg)" color="var(--info)">
                          ❄ Cold-climate spec
                        </Chip>
                      )}
                    </div>
                  </div>
                  {v.id === activeId ? (
                    <Chip bg="var(--saffron)" color="#1B1713">
                      Active
                    </Chip>
                  ) : (
                    // Apr 27 (audit P1) — Nav: open device map app at the visit address.
                    // Falls back to setting it active if no address is available.
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => {
                        const addr = v.address ?? (v as { customerAddress?: string }).customerAddress ?? '';
                        if (addr) {
                          window.open(
                            `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(String(addr))}`,
                            '_blank',
                            'noopener',
                          );
                        } else {
                          setActiveId(v.id);
                        }
                      }}
                    >
                      Nav
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active ticket stepper */}
        <div>
          <div className="card" style={{ padding: 18 }}>
            {current ? (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 14 }}>
                  <div>
                    <div className="kpi-label" style={{ marginBottom: 4 }}>
                      Active ticket · {current.ticketNumber ?? current.id}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 500 }}>{getCustomer(current)}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 3 }}>
                      {current.address ?? current.location ?? '—'}
                    </div>
                  </div>
                  <div style={{ flex: 1 }} />
                  {current.phone && (
                    <button
                      className="btn btn-outline btn-sm"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <Phone size={11} /> Call
                    </button>
                  )}
                  <button className="btn btn-primary btn-sm" style={{ marginLeft: 6 }}>
                    Start visit
                  </button>
                </div>

                <div className="kpi-label" style={{ marginBottom: 10 }}>
                  11-step workflow · {currentStep}/11 complete
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    marginBottom: 16,
                  }}
                >
                  {INSTALL_STEPS.map((s, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '7px 10px',
                        background: i === currentStep ? 'var(--saffron-50)' : 'transparent',
                        borderRadius: 4,
                        borderLeft:
                          i === currentStep ? '3px solid var(--saffron)' : '3px solid transparent',
                      }}
                    >
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 99,
                          border: i < currentStep ? 'none' : '1.5px solid var(--ink-3)',
                          background:
                            i < currentStep
                              ? 'var(--forest-600)'
                              : i === currentStep
                              ? 'var(--saffron)'
                              : 'transparent',
                          color: i <= currentStep ? 'var(--ivory-0)' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {i < currentStep ? '✓' : i + 1}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: i === currentStep ? 500 : 400,
                          color: i < currentStep ? 'var(--ink-3)' : 'var(--ink-0)',
                          flex: 1,
                          textDecoration: i < currentStep ? 'line-through' : 'none',
                        }}
                      >
                        {s}
                      </div>
                      {i === currentStep && current && (
                        // Apr 27 (audit P0) — wired to ticket detail where the
                        // 11-step state machine actually advances. Inline state
                        // increment was rejected because step transitions hit
                        // the backend SLA + workflow validations.
                        <button
                          className="btn btn-sm"
                          onClick={() => navigate(`/service-tickets/${current.id}`)}
                          style={{
                            background: 'var(--ink-0)',
                            color: 'var(--ivory-0)',
                            border: '1px solid var(--ink-0)',
                          }}
                        >
                          Mark done
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 8,
                  }}
                >
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    <Camera size={12} style={{ marginRight: 6 }} /> Photo evidence
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    <MapPin size={12} style={{ marginRight: 6 }} /> GPS OTP verify
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    <Package size={12} style={{ marginRight: 6 }} /> Request parts
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    <Wallet size={12} style={{ marginRight: 6 }} /> Submit expense
                  </button>
                  <button
                    className="btn btn-ai btn-sm"
                    style={{ justifyContent: 'flex-start' }}
                    onClick={() => burn(3, 'translate_hi')}
                  >
                    <Sparkles size={11} style={{ marginRight: 6 }} /> Translate HI · 3 cr
                  </button>
                </div>
              </>
            ) : (
              <NoData label="Select a visit to see its 11-step workflow" />
            )}
          </div>
        </div>

        {/* Right rail */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <IncentiveBar data={incentive} />

          {coldClimateAllowed && coldClimateVisible && (
            <div
              className="card"
              style={{
                padding: 12,
                background: 'var(--info-bg)',
                borderLeft: '3px solid var(--info)',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--info)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.06,
                }}
              >
                ❄ Cold-climate visit today
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-2)', marginTop: 4 }}>
                Carry cold-weather PPE and insulated tool kit. Pressure test at min 4°C.
              </div>
            </div>
          )}

          <WarrantyBarcodeInput />

          <div className="card" style={{ padding: 14 }}>
            <div className="kpi-label" style={{ marginBottom: 10 }}>
              This month
            </div>
            {statsLoading ? (
              <Skeleton h={90} />
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 10,
                  fontSize: 12,
                }}
              >
                <div>
                  <div className="num-display" style={{ fontSize: 20 }}>
                    {formatNum(stats?.visitsDone ?? 0)}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Visits done</div>
                </div>
                <div>
                  <div className="num-display" style={{ fontSize: 20, color: 'var(--forest-600)' }}>
                    {stats?.onTimePct != null ? `${stats.onTimePct}%` : '—'}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>On-time</div>
                </div>
                <div>
                  <div className="num-display" style={{ fontSize: 20 }}>
                    {stats?.avgRating != null ? stats.avgRating.toFixed(1) : '—'}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Avg rating</div>
                </div>
                <div>
                  <div className="num-display" style={{ fontSize: 20 }}>
                    {formatINRCompact(stats?.expenses ?? 0)}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Expenses</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
    </div>
  );
}

export default ServiceTechDashboard;
